import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Typography,
    Alert,
    Button,
    Grid,
    Divider,
    Paper,
    Chip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Feedback, FeedbackService } from './services/FeedbackService.ts';
import SnackbarComponent from '../../common/components/SnackbarComponent.tsx';

const feedbackService = new FeedbackService();

const TYPE_SV: Record<string, string> = {
    'General Feedback': 'Allmän feedback',
    'Bug report': 'Felrapport',
    'Feature request': 'Funktionsförslag',
    Feature: 'Funktionsförslag',
};

const CATEGORY_SV: Record<string, string> = {
    General: 'Allmänt',
    'Barcode scanning': 'Streckkodsskanning',
    'Inventory management': 'Lagerhantering',
    'Dose bag management': 'Dospåshantering',
    Authentication: 'Inloggning',
    'User interface': 'Användargränssnitt',
    Performance: 'Prestanda',
    Other: 'Övrigt',
};

const PRIORITY_SV: Record<string, string> = {
    Low: 'Låg',
    Medium: 'Medium',
    High: 'Hög',
    Critical: 'Kritisk',
};

const FeedbackDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [feedback, setFeedback] = useState<Feedback | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string>('new');
    const [note, setNote] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [saved, setSaved] = useState<boolean>(false);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMsg, setSnackbarMsg] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'error' | 'warning' | 'info'
    >('success');

    const STATUS_SV: Record<string, string> = {
        new: 'Ny',
        in_progress: 'Pågående',
        closed: 'Avslutad',
    };
    const STATUS_ORDER = ['new', 'in_progress', 'closed'];
    const STATUS_COLOR: Record<string, 'default' | 'info' | 'success'> = {
        new: 'default',
        in_progress: 'info',
        closed: 'success',
    };

    useEffect(() => {
        if (id) {
            loadFeedback(id);
        }
    }, [id]);

    const loadFeedback = async (feedbackId: string) => {
        try {
            setLoading(true);
            const data = await feedbackService.getById(feedbackId);
            setFeedback(data);
            setStatus(data.status ?? 'new');
            setNote(data.note ?? '');
        } catch (err) {
            setError('Kunde inte hämta feedback detaljer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !feedback) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">
                    {error || 'Feedback hittades inte'}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                >
                    Tillbaka
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Tillbaka
                </Button>
                <Chip
                    label={STATUS_SV[status] ?? status}
                    size="small"
                    color={STATUS_COLOR[status] ?? 'default'}
                    variant="filled"
                />
            </Stack>

            <Card>
                <CardContent>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography variant="h5">{feedback.title}</Typography>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mb: 2, flexWrap: 'wrap' }}
                    >
                        <Chip
                            label={TYPE_SV[feedback.type] ?? feedback.type}
                            size="small"
                            variant="outlined"
                        />
                        <Chip
                            label={
                                PRIORITY_SV[feedback.priority] ??
                                feedback.priority
                            }
                            size="small"
                            color={
                                feedback.priority === 'Critical'
                                    ? 'error'
                                    : feedback.priority === 'High'
                                      ? 'warning'
                                      : feedback.priority === 'Medium'
                                        ? 'info'
                                        : 'default'
                            }
                        />
                        <Chip
                            label={
                                CATEGORY_SV[feedback.category] ??
                                feedback.category
                            }
                            size="small"
                            variant="outlined"
                        />
                        {feedback.appVersion && (
                            <Chip
                                label={`Version: ${feedback.appVersion}`}
                                size="small"
                            />
                        )}
                        {feedback.platform && (
                            <Chip
                                label={`Plattform: ${feedback.platform}`}
                                size="small"
                            />
                        )}
                    </Stack>

                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 1 }}
                        >
                            Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                >
                                    Användare
                                </Typography>
                                <Typography variant="body1">
                                    {feedback.userName} ({feedback.userEmail})
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                >
                                    Datum
                                </Typography>
                                <Typography variant="body1">
                                    {feedback.timestamp
                                        ? new Date(
                                              feedback.timestamp
                                          ).toLocaleString('sv-SE')
                                        : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                >
                                    Tenant
                                </Typography>
                                <Typography variant="body1">
                                    {feedback.tenantId}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                >
                                    Kategori
                                </Typography>
                                <Typography variant="body1">
                                    {CATEGORY_SV[feedback.category] ??
                                        feedback.category}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Divider sx={{ my: 2 }} />

                    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 1 }}
                        >
                            Status och anteckning
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        value={status}
                                        onChange={(e) =>
                                            setStatus(e.target.value)
                                        }
                                        sx={{
                                            '& .MuiSelect-select': { py: 1.1 },
                                        }}
                                    >
                                        {STATUS_ORDER.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {STATUS_SV[s]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Anteckning"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color={saved ? 'success' : 'primary'}
                                    fullWidth
                                    disabled={saving}
                                    startIcon={
                                        saving ? (
                                            <CircularProgress size={18} />
                                        ) : saved ? (
                                            <CheckCircleOutlineIcon />
                                        ) : undefined
                                    }
                                    onClick={async () => {
                                        if (!feedback) return;
                                        try {
                                            setSaving(true);
                                            setSaved(false);
                                            await feedbackService.updateStatus(
                                                feedback.id,
                                                status,
                                                note
                                            );
                                            const updated =
                                                await feedbackService.getById(
                                                    feedback.id
                                                );
                                            setFeedback(updated);
                                            setSaving(false);
                                            setSaved(true);
                                            setSnackbarMsg(
                                                'Status och anteckning sparade'
                                            );
                                            setSnackbarSeverity('success');
                                            setSnackbarOpen(true);
                                            setTimeout(
                                                () => setSaved(false),
                                                2000
                                            );
                                        } catch (err) {
                                            console.error(err);
                                            setSaving(false);
                                            setSaved(false);
                                            setSnackbarMsg(
                                                'Kunde inte spara status/anteckning.'
                                            );
                                            setSnackbarSeverity('error');
                                            setSnackbarOpen(true);
                                            setError(
                                                'Kunde inte spara status/anteckning.'
                                            );
                                        }
                                    }}
                                >
                                    {saving
                                        ? 'Sparar…'
                                        : saved
                                          ? 'Sparat'
                                          : 'Spara'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Typography variant="h6" gutterBottom>
                        Beskrivning
                    </Typography>
                    <Typography
                        variant="body1"
                        paragraph
                        style={{ whiteSpace: 'pre-wrap' }}
                    >
                        {feedback.description}
                    </Typography>

                    {feedback.stepsToReproduce && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                Steg för att återskapa
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {feedback.stepsToReproduce}
                                </Typography>
                            </Paper>
                        </>
                    )}

                    {feedback.expectedBehavior && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                Förväntat beteende
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                style={{ whiteSpace: 'pre-wrap' }}
                            >
                                {feedback.expectedBehavior}
                            </Typography>
                        </>
                    )}

                    {feedback.actualBehavior && (
                        <>
                            <Typography variant="h6" gutterBottom>
                                Faktiskt beteende
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                style={{ whiteSpace: 'pre-wrap' }}
                            >
                                {feedback.actualBehavior}
                            </Typography>
                        </>
                    )}
                </CardContent>
            </Card>
            <SnackbarComponent
                open={snackbarOpen}
                message={snackbarMsg}
                severity={snackbarSeverity}
                autoHideDuration={2500}
                onClose={() => setSnackbarOpen(false)}
            />
        </Container>
    );
};

export default FeedbackDetail;
